/**
 * "How many doctors did this plan name?" — ONE implementation.
 *
 * The fact renders in five places (the month grid, the planner's own subtitle,
 * the team card, the all-plans card, the detail page), and before this it was
 * five copies of `(e.planned_doctors?.length || 0)`. Every copy printed the
 * same wrong thing about the same rows: a plan written before `planned_doctors`
 * existed named NOBODY, and `|| 0` turned that hole in the record into the
 * sentence "0 doctors" — a statement about what the officer planned, made out
 * of a field that was never written. One copy did not even have the `?.` and
 * threw on the same rows.
 *
 * The server now answers both questions separately (see `routes_tour_plans.
 * _read_plan`): `planned_doctor_count` is `null` when days were planned and not
 * one of them named anyone, and each entry carries `doctors_named`. These
 * helpers are the only place either is turned into words.
 *
 * A month with NO days planned counts 0, not null. Zero days plan zero doctors
 * by definition — there is no day whose list is missing — so an untouched month
 * must not open claiming a gap.
 *
 * Pattern A, `lib/vocabulary.js`: a missing record is never rendered as a fact.
 */
import { count, plural } from "../../lib/vocabulary";
import CategoryBadge from "../../components/doctors/CategoryBadge";

/** Nobody wrote a doctor list — not "nobody was planned". */
export const DOCTORS_NOT_NAMED = "Doctors not named";
export const DOCTORS_NOT_NAMED_HELP =
  "This plan was written before plans named their doctors, so there is nothing to compare visits against. It is not a record that no doctors were planned.";

/**
 * The plan-level count: "6 doctors", or the gap in words.
 * Never "0 doctors" derived from an absent field.
 */
export function plannedDoctorsLabel(plan) {
  const n = plan?.planned_doctor_count;
  if (n === null || n === undefined) return DOCTORS_NOT_NAMED;
  return plural(n, "doctor");
}

/** The same, for a single day. `doctors_named` is the server's flag. */
export function entryDoctorsLabel(entry) {
  if (!entry?.doctors_named) return DOCTORS_NOT_NAMED;
  return plural((entry.planned_doctors || []).length, "doctor");
}

/**
 * A count for a stat position, where `—` is the right shape for absent.
 * `count()` renders null as the dash rather than as a zero.
 */
export function plannedDoctorsCount(plan) {
  return count(plan?.planned_doctor_count);
}

/**
 * What a chip says when the id resolves to nothing.
 *
 * A doctor archived and removed from the master after the plan was written is
 * absent from `doctor_lookup`. The reviewer is told that, in words. NEVER a
 * blank chip (which reads as a rendering bug), never the raw uuid (which is not
 * a doctor's name and cannot be acted on), and never a dropped chip — the day
 * planned that many calls and the count beside it must still add up.
 */
export const DOCTOR_NOT_LISTED = "Doctor no longer listed";

/**
 * The doctors one day names, by name, with the A/B/C beside each.
 *
 * The category travels with the name because A/B/C is WHY a call is worth
 * making — a route of three C-category doctors and a route of three A's are
 * different plans, and a reviewer cannot tell them apart from names alone.
 *
 * `lookup` is keyed by id and served once per plan (`routes_tour_plans.
 * doctor_lookup_for_plan`), not inlined per day: a doctor appears on several
 * dates in a month.
 *
 * A day that never named a doctor (`doctors_named: false`) draws NO chips. Its
 * label already says "Doctors not named" and a row of nothing underneath would
 * read as "named nobody" — the F313 distinction this whole module exists for.
 */
export function PlannedDoctorChips({ entry, lookup }) {
  if (!entry?.doctors_named) return null;
  const ids = entry.planned_doctors || [];
  if (!ids.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`planned-doctors-${entry.date}`}>
      {ids.map((id, i) => {
        const doc = (lookup || {})[id];
        return (
          <span
            key={`${id}-${i}`}
            data-testid={doc ? `planned-doctor-${id}` : `planned-doctor-unresolved-${id}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] ${
              doc ? "border-callisto bg-white text-callisto-navy font-semibold"
                  : "border-dashed border-slate-300 bg-slate-50 text-callisto-muted italic"
            }`}
          >
            {doc ? doc.name : DOCTOR_NOT_LISTED}
            {doc?.category && <CategoryBadge category={doc.category} size="sm" />}
          </span>
        );
      })}
    </div>
  );
}
